import { useState, useMemo, useEffect } from "react";
import { ChevronRight, Folder, File, Loader2, Search, FileText, Table, AlignLeft, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useGoogleDriveFiles } from "@/hooks/cloud/useGoogleDriveFiles";
import { useOneDriveFiles } from "@/hooks/cloud/useOneDriveFiles";
import { CloudFile, CloudFolder, CloudProvider } from "@/types/cloud";
import { Badge } from "@/components/ui/badge";
import { DocumentTypeIconMap } from "@/types/knowledge/knowledge-base";

interface CloudFileBrowserProps {
  open: boolean;
  onClose: () => void;
  onFilesSelected: (files: CloudFile[]) => void;
  provider?: CloudProvider;
}

const CloudFileBrowser = ({ open, onClose, onFilesSelected, provider = "google_drive" }: CloudFileBrowserProps) => {
  const getRootFolderName = (provider: CloudProvider) => {
    return provider === "google_drive" ? "My Drive" : "Root";
  };

  const [folderStack, setFolderStack] = useState<{ id: string; name: string }[]>([
    { id: "root", name: getRootFolderName(provider) },
  ]);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Reset folder stack when provider changes or drawer closes
  useEffect(() => {
    if (open) {
      setFolderStack([{ id: "root", name: getRootFolderName(provider) }]);
    }
  }, [provider, open]);

  const currentFolderId = folderStack[folderStack.length - 1].id;
  const googleDriveFiles = useGoogleDriveFiles(currentFolderId);
  const oneDriveFiles = useOneDriveFiles(currentFolderId);
  const { files, folders, isLoading } = provider === "google_drive" ? googleDriveFiles : oneDriveFiles;


  const getFileIcon = (file: CloudFile) => {
    const iconConfig = DocumentTypeIconMap[file.mimeType as keyof typeof DocumentTypeIconMap];
    if (!iconConfig) return <File className="h-5 w-5 text-gray-400 flex-shrink-0" />;

    const iconMap: Record<string, React.ComponentType<any>> = {
      FileText,
      Table,
      AlignLeft,
      Play,
      File,
    };

    const IconComponent = iconMap[iconConfig.icon as keyof typeof iconMap];
    if (!IconComponent) return <File className="h-5 w-5 text-gray-400 flex-shrink-0" />;

    return <IconComponent className={`h-5 w-5 flex-shrink-0 ${iconConfig.color}`} />;
  };

  // Filter files and folders based on search query
  const filteredFiles = useMemo(() => {
    if (!searchQuery) return files;
    return files.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [files, searchQuery]);

  const filteredFolders = useMemo(() => {
    if (!searchQuery) return folders;
    return folders.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [folders, searchQuery]);

  const handleFolderClick = (folder: CloudFolder) => {
    setFolderStack([...folderStack, { id: folder.id, name: folder.name }]);
  };

  const handleBackClick = () => {
    if (folderStack.length > 1) {
      setFolderStack(folderStack.slice(0, -1));
    }
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelected = new Set(selectedFileIds);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFileIds(newSelected);
  };

  const selectedFiles = files.filter((f) => selectedFileIds.has(f.id));

  const handleImport = () => {
    onFilesSelected(selectedFiles);
    onClose();
    setSelectedFileIds(new Set());
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col h-full">
        <SheetHeader>
          <SheetTitle>Import from {provider === "google_drive" ? "Google Drive" : "OneDrive"}</SheetTitle>
          <SheetDescription>Browse and select files to add to your knowledge base</SheetDescription>
        </SheetHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-1 mb-4 pb-2 border-b text-sm overflow-x-auto">
            {folderStack.map((folder, index) => (
              <div key={folder.id} className="flex items-center gap-1 whitespace-nowrap">
                {index > 0 && <ChevronRight className="h-4 w-4 text-gray-400" />}
                <button
                  onClick={() => {
                    setFolderStack(folderStack.slice(0, index + 1));
                  }}
                  className={`${
                    index === folderStack.length - 1 ? "text-gray-900 font-semibold" : "text-genie-primary hover:underline"
                  }`}
                >
                  {folder.name}
                </button>
              </div>
            ))}
          </div>

          {/* Search Input */}
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* File List */}
          <div className="flex-1 overflow-y-auto min-h-0 space-y-1 pr-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-genie-primary" />
              </div>
            ) : (
              <>
                {/* Folders */}
                {filteredFolders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => handleFolderClick(folder)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left group"
                  >
                    <Folder className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <span className="flex-1 truncate text-gray-900">{folder.name}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                  </button>
                ))}

                {/* Files */}
                {filteredFiles.map((file) => (
                  <label
                    key={file.id}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFileIds.has(file.id)}
                      onChange={() => toggleFileSelection(file.id)}
                      className="h-5 w-5 rounded border-gray-300 text-genie-primary"
                    />
                    {getFileIcon(file)}
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-gray-900 text-sm">{file.name}</p>
                      {file.size && (
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                  </label>
                ))}

                {filteredFiles.length === 0 && filteredFolders.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                    <File className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">No files or folders found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t pt-4 mt-4 flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={selectedFileIds.size === 0 || isLoading}
              className="bg-gradient-to-r from-genie-primary to-genie-secondary text-white"
            >
              Import {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ""}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {selectedFiles.length > 0 && (
              <>
                <Badge variant="secondary">{selectedFiles.length} selected</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedFileIds(new Set())}
                  className="text-xs"
                >
                  Clear
                </Button>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CloudFileBrowser;
